export interface Debouncer<T extends any[], U extends any = unknown> {
	(this: U, ...args: T): void;

	runNow(this: U, ...args: T): void;
	clear(additionalAction?: () => void): void;
}

/**
 * @param fn the function to debounce
 * @param delay the delay in milliseconds, undefined, or a function that takes the arguments passed to fn and returns a delay in milliseconds or undefined.
 *              if undefined, then a call to {@link window.requestAnimationFrame} is used instead of {@link window.setTimeout}
 * @param [eager=false] if true, the debounced function will be called before starting the delay instead of after the delay has expired
 * @param [forceExactDelay=false] if true, then calls to the debounced function while the delay is active will not reset the delay
 * @return a debounced version of the given function
 */
export default function debounce<T extends any[], U extends any = unknown>(
	fn: (this: U, ...args: T) => void, delay: number|undefined|((this: U, ...args: T) => number|undefined) = 0, eager = false, forceExactDelay = false
): Debouncer<T, U> {
	// Derived from just-debounce with modifications to allow the delay to be a function of the arguments
	let timeout: number|undefined;
	let timeoutIsAnimationFrame = false;
	let args: T;
	let self: any;

	const internalClear = () => {
		if (timeout !== undefined) {
			(timeoutIsAnimationFrame ? window.cancelAnimationFrame : window.clearTimeout)(timeout);
			timeout = undefined;
		}
	};
	const internalRun = () => {
		internalClear();
		fn.apply(self, args);
	};

	const res = function debounced(this: U, ...currentArgs: T) {
		self = this;
		args = Object.assign([], currentArgs); // This is a type-safe alternative to the spread operator

		if (!timeout || !(eager || forceExactDelay)) {
			const d = typeof delay !== 'function' ? delay : delay.apply(self, args);
			if (eager) {
				timeoutIsAnimationFrame = d === undefined;
				if (d === undefined) {
					timeout = window.requestAnimationFrame(() => res.clear());
				} else {
					timeout = window.setTimeout(res.clear, d);
				}
				fn.apply(self, args);
			} else {
				res.clear();
				timeoutIsAnimationFrame = d === undefined;
				if (d === undefined) {
					timeout = window.requestAnimationFrame(internalRun);
				} else {
					timeout = window.setTimeout(internalRun, d);
				}
			}
		}
	};
	res.runNow = function(this: U, ...args: T) {
		internalClear();
		fn.apply(this, args);
	};
	res.clear = function(additionalAction?: () => void) {
		internalClear();
		if (additionalAction) {
			additionalAction();
		}
	};
	return res;
}
